import type { AxiosRequestConfig } from "axios";
import type { User } from "../model";
import type { Account, AccountDto } from "../model/Account";
import type { AccountSearchOption } from "../model/AccountSearchOption";
import type { AuditEvent } from "../model/AuditEvent";
import type { FieldValueObject } from "../model/FieldDefinition";
import type { AssignedRoleUserDto } from "../types";
import API, { responseOf } from "./base";

export const create = (account: Account): Promise<Account> =>
  responseOf(API.post("/accounts", account));

export const get = (accountId: string): Promise<Partial<AccountDto>> =>
  responseOf(API.get(`/accounts/${accountId}`));

export const update = (account: Account): Promise<Account> =>
  responseOf(API.put(`/accounts/${account.id}`, account));

// Delete not allowed as variable name
const remove = async (accountId: string): Promise<void> => {
  await API.delete(`/accounts/${accountId}`);
};
export { remove as delete };

export const activeByOrganizationId = async (
  organizationId: string,
  options: AxiosRequestConfig = {},
): Promise<AccountDto[] | Blob> => {
  const { data } = await API.get(
    `/accounts/byOrganizationId/${organizationId}/active`,
    options,
  );

  return options.format ? data : data.payload;
};

export const byOrganizationId = async (
  organizationId: string,
  options: AxiosRequestConfig = {},
): Promise<AccountDto[]> => {
  const { data } = await API.get(
    `/accounts/byOrganizationId/${organizationId}`,
    options,
  );

  return options.format ? data : data.payload;
};

export const byOrganizationAndAccountType = async (
  organizationId: string,
  accountTypeId: string,
  options: AxiosRequestConfig = {},
): Promise<AccountDto[]> => {
  const { data } = await API.get(
    `/accounts/byOrganizationId/${organizationId}/byAccountType/${accountTypeId}`,
    options,
  );

  return options.format ? data : data.payload;
};

export const listAccountsByMember = (userId: string): Promise<AccountDto[]> =>
  responseOf(API.get(`/accounts/byMember/${userId}`));

export const auditEventsBySubjectId = (
  subjectId: string,
): Promise<AuditEvent[]> =>
  responseOf(API.get(`/accounts/${subjectId}/audit-events`));

export const listUsersByAccountId = (accountId: string): Promise<User[]> =>
  responseOf(API.get(`/accounts/${accountId}/users`));

export const listTeamMembersByAccountId = (
  accountId: string,
): Promise<AssignedRoleUserDto[]> =>
  responseOf(API.get(`/accounts/${accountId}/teamMembers`));

export const assignAccountRole = async (
  accountId: string,
  userId: string,
  roleId: string,
): Promise<void> => {
  await API.post("/accounts/assignAccountRole", {
    userId,
    roleId,
    accountId,
  });
};

export const unassignAccountRole = async (
  accountId: string,
  userId: string,
  roleId: string,
): Promise<void> => {
  await API.post("/accounts/unAssignAccountRole", {
    userId,
    roleId,
    accountId,
  });
};

export const removeUser = async (
  accountId: string,
  userId: string,
): Promise<void> => {
  await API.delete(`/accounts/${accountId}/users/${userId}`);
};

export const addUser = (accountId: string, userId: string): Promise<boolean> =>
  responseOf(API.put(`/accounts/${accountId}/users/${userId}`));

export const getShareKey = (accountId: string): Promise<string> =>
  responseOf(API.get(`/accounts/${accountId}/shareKey`));

export const createShareKey = (accountId: string): Promise<string> =>
  responseOf(API.post(`/accounts/${accountId}/shareKey`));

export const setFieldValue = async (
  accountId: string,
  fieldDefinitionId: string,
  value: FieldValueObject,
): Promise<void> => {
  await API.post(`/accounts/${accountId}/fields/${fieldDefinitionId}`, value);
};

export const deleteFieldValue = async (
  accountId: string,
  fieldDefinitionId: string,
): Promise<void> => {
  await API.delete(`/accounts/${accountId}/fields/${fieldDefinitionId}`);
};

export const getFieldValue = (
  accountId: string,
  fieldDefinitionId: string,
): Promise<{ value: FieldValueObject; updated_at?: number }> =>
  responseOf(API.get(`/accounts/${accountId}/fields/${fieldDefinitionId}`));

export const search = async (
  query: string,
  organizationId?: string,
  accountTypeId?: string,
): Promise<AccountSearchOption[]> => {
  try {
    return await responseOf(
      API.post("/accounts/search", {
        query,
        organizationId,
        accountTypeId,
      }),
    );
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const directorySearch = async (
  query: string,
  accountTypeId?: string,
  exclude?: string[],
  organizationId?: string,
): Promise<AccountSearchOption[]> => {
  try {
    return await responseOf(
      API.post("/accounts/directory-search", {
        query,
        accountTypeId,
        exclude,
        organizationId,
      }),
    );
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const searchExternal = (
  query: string,
  accountTypeId: string,
): Promise<Partial<AccountDto>[]> =>
  responseOf(
    API.post("/accounts/search-external", {
      query,
      accountTypeId,
    }),
  );

export const getByOrganizationAndExternalOrganizationId = async (
  organizationId: string,
  externalOrganizationId: string,
): Promise<AccountDto[]> =>
  (await responseOf(
    API.get(
      `/accounts/byOrganizationId/${organizationId}/byExternalOrgId/${externalOrganizationId}`,
    ),
  )) || [];
